import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Save,
  Truck,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useIsReadOnly } from '@/shared/auth/SessionProvider';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { FileLink } from '@/shared/components/file-upload/FileLink';
import { FileUploader } from '@/shared/components/file-upload/FileUploader';
import { listEntityFiles } from '@/shared/api/documents';
import { cn } from '@/shared/lib/cn';
import {
  anularDetalleRecepcion,
  finalizarRecepcion,
  getRecepcionDetail,
  recibirRecepcion,
  type DetalleRow,
  type FinalizarRecepcionResponse,
  type RecepcionRow,
  type RecibirResponse,
  type RemitoRow,
} from '../api';

/**
 * Detail view for a recepción. Cada "Registrar entrega" es un remito: se
 * cargan las cantidades de ESA entrega (esperada / ya recibida / pendiente
 * por línea), con Nº de remito y fecha, y el stock pasa de Por Ingresar a
 * Disponible. Muestra el historial de entregas, permite anular líneas sin
 * mercadería recibida y finalizar la recepción.
 */
export function DetalleRecepcionDialog({
  summary,
  onClose,
}: {
  summary: RecepcionRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const detailQ = useQuery({
    queryKey: ['recepcion', 'detail', summary.id],
    queryFn: () => getRecepcionDetail(summary.id),
    staleTime: 15_000,
  });
  // Multi-file mode: enumerate every drive item under
  // `Recepciones/{summary.id}/`. The folder is the source of truth —
  // re-runs after each upload/delete via `qc.invalidateQueries` so the
  // list updates without a full page reload.
  const filesQ = useQuery({
    queryKey: ['recepcion', 'files', summary.id],
    queryFn: () => listEntityFiles('Recepcion', summary.id),
    staleTime: 15_000,
  });
  const refreshFiles = () =>
    qc.invalidateQueries({ queryKey: ['recepcion', 'files', summary.id] });

  const isVisitante = useIsReadOnly();
  const readOnly =
    isVisitante ||
    (summary.fields.Status_RR ?? '') === 'Recibido' ||
    (summary.fields.Status_RR ?? '') === 'Anulado';

  // Documentación section is collapsable so a recepción with many
  // attachments doesn't push the rest of the dialog (líneas + finalize
  // banner) below the fold. Open by default — the file list itself
  // scrolls inside a max-height window when there are more than ~3-4
  // files.
  const [docsOpen, setDocsOpen] = useState(true);
  // Entrega en curso: cantidades de ESTE remito por línea (detalleId → texto).
  const [cantidades, setCantidades] = useState<Record<string, string>>({});
  // Nº de serie por unidad de ESTA entrega (detalleId → un campo por unidad), sólo hornos y opcional.
  const [seriesPorLinea, setSeriesPorLinea] = useState<Record<string, string[]>>({});
  const [nroRemito, setNroRemito] = useState('');
  const [fecha, setFecha] = useState(hoyLocalIso);
  const [nroDespacho, setNroDespacho] = useState('');

  const activeDetalles = useMemo(
    () =>
      (detailQ.data?.detalles ?? []).filter(
        (d) => (d.fields.Status_DR ?? '') !== 'Anulado',
      ),
    [detailQ.data],
  );
  const anuladas = useMemo(
    () =>
      (detailQ.data?.detalles ?? []).filter(
        (d) => (d.fields.Status_DR ?? '') === 'Anulado',
      ),
    [detailQ.data],
  );

  const totalEntrega = useMemo(
    () =>
      activeDetalles.reduce((sum, d) => sum + parseNumber(cantidades[d.id] ?? ''), 0),
    [activeDetalles, cantidades],
  );
  const hayEntrega = totalEntrega > 0;
  // Una cantidad con decimales o mayor a lo pendiente frena el envío (el server también).
  const hayCantidadInvalida = activeDetalles.some(
    (d) => errorCantidad(parseNumber(cantidades[d.id] ?? ''), d.cantidadEsperada, d.cantidadRecibida) != null,
  );
  // Una serie repetida dentro de la misma línea frena el envío (el server frena la que ya existe en stock).
  const haySerieRepetida = activeDetalles.some(
    (d) =>
      seriesRepetidas(seriesDeEntrega(seriesPorLinea[d.id], parseNumber(cantidades[d.id] ?? ''))).size > 0,
  );
  const hayError = hayCantidadInvalida || haySerieRepetida;
  const entregaLista = hayEntrega && !hayError && nroRemito.trim().length > 0 && fecha.length > 0;

  const saveMutation = useMutation<RecibirResponse | FinalizarRecepcionResponse, Error, boolean>({
    mutationFn: (finalize: boolean) => {
      // Sin cantidades nuevas, finalizar es sólo cerrar la recepción.
      if (!hayEntrega) return finalizarRecepcion(summary.id);
      const detalles = activeDetalles
        .map((d) => {
          const cantidad = parseNumber(cantidades[d.id] ?? '');
          const series = seriesDeEntrega(seriesPorLinea[d.id], cantidad)
            .map((s) => s.trim())
            .filter(Boolean);
          return {
            detalleId: d.id,
            cantidad,
            ...(series.length > 0 ? { series } : {}),
          };
        })
        .filter((d) => d.cantidad > 0);
      return recibirRecepcion(summary.id, {
        nroRemito: nroRemito.trim(),
        fecha,
        nroDespacho: nroDespacho.trim() || undefined,
        detalles,
        finalize,
      });
    },
    onSuccess: (r, finalize) => {
      if (r.outcome === 'blocked_status') {
        toast.info('No aplicable', { description: r.message });
        return;
      }
      if (r.outcome === 'finalized') {
        toast.success('Recepción finalizada');
      } else {
        const movido = r.lineas.reduce((n, l) => n + l.movido, 0);
        toast.success(finalize ? 'Entrega registrada y recepción finalizada' : 'Entrega registrada', {
          description: r.mueveStock
            ? `${movido} un. pasaron de Por Ingresar a Disponible.`
            : 'Recepción anterior al ingreso automático: no se movió stock.',
        });
        // Lo que no se movió: o la línea no tiene artículo, o Por Ingresar no lo cubría.
        const sinArticulo = r.lineas.filter((l) => l.sinArticulo).length;
        const noMovido = r.lineas
          .filter((l) => !l.sinArticulo)
          .reduce((n, l) => n + l.noMovido, 0);
        if (r.mueveStock && (noMovido > 0 || sinArticulo > 0)) {
          toast.info('Parte de lo recibido no movió stock', {
            description: [
              noMovido > 0
                ? `${noMovido} un. no estaban en Por Ingresar (ya asignadas a ventas o recibidas de más).`
                : null,
              sinArticulo > 0
                ? `${sinArticulo} línea(s) sin artículo asociado: no mueven stock.`
                : null,
            ]
              .filter(Boolean)
              .join(' '),
            duration: 10_000,
          });
        }
        setCantidades({});
        setSeriesPorLinea({});
        setNroRemito('');
      }
      qc.invalidateQueries({ queryKey: ['recepcion'] });
      qc.invalidateQueries({ queryKey: ['inventario'] });
      if (finalize || r.outcome === 'finalized') onClose();
    },
    onError: (err) =>
      toast.error('No se pudo guardar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  const anularMutation = useMutation({
    mutationFn: (detalleId: string) =>
      anularDetalleRecepcion(summary.id, detalleId),
    onSuccess: (r) => {
      if (r.outcome === 'anulado' || r.outcome === 'already_anulado') {
        // When the backend reports the header was auto-anulated (last
        // remaining line just went), close the dialog so the user lands
        // back on the gallery and sees the row flip to Anulado.
        const headerAnulada = r.outcome === 'anulado' && r.recepcionAnulada;
        toast.success(
          headerAnulada
            ? 'Recepción anulada (no quedaban líneas activas)'
            : 'Línea anulada',
        );
        qc.invalidateQueries({ queryKey: ['recepcion'] });
        if (headerAnulada) onClose();
      }
    },
    onError: (err) =>
      toast.error('No se pudo anular la línea', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  // Confirmation gate when the user is about to anular the LAST active
  // line — without it, the user lands on a recepción with zero líneas and
  // /finalizar fails with invalid_body (server requires at least one
  // detalle). The backend now also auto-anulates the header in that case;
  // this dialog just makes sure the user knows it's about to happen.
  const [confirmAnular, setConfirmAnular] = useState<DetalleRow | null>(null);
  const handleAnularRequest = (detalle: DetalleRow) => {
    if (activeDetalles.length === 1 && detalle.id === activeDetalles[0]?.id) {
      setConfirmAnular(detalle);
      return;
    }
    anularMutation.mutate(detalle.id);
  };

  const busy = saveMutation.isPending || anularMutation.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent
        className="max-w-4xl"
        processing={
          saveMutation.isPending
            ? saveMutation.variables
              ? 'Finalizando recepción…'
              : 'Registrando entrega…'
            : false
        }
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-4 w-4" aria-hidden />
            Recepción #{summary.id}
            {summary.compra && (
              <span className="text-xs font-normal text-muted-foreground">
                · Compra #{summary.compra.id}
                {summary.compra.nroFactura && ` · Fc ${summary.compra.nroFactura}`}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2">
            <StatusBadge status={(summary.fields.Status_RR ?? '') as string} />
            {summary.compra?.proveedor && (
              <span className="text-sm">{summary.compra.proveedor}</span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {detailQ.isPending ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : detailQ.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              No se pudo cargar el detalle.
            </div>
          ) : (
            <>
              <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
                {!readOnly && detailQ.data.mueveStock === false && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>
                      Recepción anterior al ingreso automático: registrar la entrega no
                      mueve stock. Ajustá Inventario con la corrección manual si hace falta.
                    </span>
                  </div>
                )}
                <div className="flex flex-wrap items-end justify-between gap-3">
                  {!readOnly ? (
                    <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-3">
                      <div>
                        <Label htmlFor="rr-remito" className="text-sm">
                          Nº de remito <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="rr-remito"
                          className="mt-1"
                          value={nroRemito}
                          onChange={(e) => setNroRemito(e.target.value)}
                          placeholder="Nº remito del proveedor"
                          maxLength={120}
                          disabled={busy}
                        />
                      </div>
                      <div>
                        <Label htmlFor="rr-fecha" className="text-sm">
                          Fecha del remito <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="rr-fecha"
                          type="date"
                          className="mt-1"
                          value={fecha}
                          max={hoyLocalIso()}
                          onChange={(e) => setFecha(e.target.value)}
                          disabled={busy}
                        />
                      </div>
                      <div>
                        <Label htmlFor="rr-despacho" className="text-sm">
                          Nº de despacho (opcional)
                        </Label>
                        <Input
                          id="rr-despacho"
                          className="mt-1"
                          value={nroDespacho}
                          onChange={(e) => setNroDespacho(e.target.value)}
                          maxLength={120}
                          disabled={busy}
                        />
                      </div>
                    </div>
                  ) : (
                    <span />
                  )}
                  <HeaderSummary detalles={activeDetalles} />
                </div>
                {/* Documentación adjunta (remito / foto / inspección).
                    Multi-file: every drive item under
                    `DocumentosRecepciones/Recepciones/{recepcionId}/`
                    is surfaced as its own pill. Editing is locked on
                    finalized (Recibido) and anulado recepciones — the
                    pills stay visible for audit, but new uploads /
                    deletes are gated on `!readOnly`.
                    Collapsable + scrollable inside so a recepción with
                    many attachments doesn't bloat the dialog. */}
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => setDocsOpen((v) => !v)}
                    className="flex w-full items-center gap-1 text-left"
                    aria-expanded={docsOpen}
                  >
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                        !docsOpen && '-rotate-90',
                      )}
                      aria-hidden
                    />
                    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Documentación
                      {filesQ.data && filesQ.data.length > 0
                        ? ` (${filesQ.data.length})`
                        : ''}
                    </span>
                  </button>
                  {docsOpen && (
                    <div className="mt-1.5 w-full min-w-0">
                      {/* Cap the file list at ~4 rows of compact pills
                          (≈40px each) before scrolling. The uploader
                          stays outside the scroll container so it's
                          always reachable. */}
                      <div className="flex max-h-44 flex-col gap-1.5 overflow-y-auto pr-1">
                        {filesQ.isPending ? (
                          <p className="text-xs text-muted-foreground">
                            Cargando archivos…
                          </p>
                        ) : filesQ.data && filesQ.data.length > 0 ? (
                          filesQ.data.map((f) => (
                            <FileLink
                              key={f.driveItemId}
                              entityType="Recepcion"
                              entityId={summary.id}
                              driveItemId={f.driveItemId}
                              fileName={f.fileName}
                              allowDelete={!readOnly}
                              onDeleted={refreshFiles}
                              compact
                            />
                          ))
                        ) : readOnly ? (
                          <p className="text-xs text-muted-foreground">
                            Sin documentación adjunta.
                          </p>
                        ) : null}
                      </div>
                      {!readOnly && (
                        <div className="mt-1.5">
                          <FileUploader
                            entityType="Recepcion"
                            entityId={summary.id}
                            compact
                            emptyHint={
                              filesQ.data && filesQ.data.length > 0
                                ? 'Agregar otro archivo'
                                : 'Subir remito, foto u otro documento'
                            }
                            onUploaded={refreshFiles}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <section className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Líneas ({activeDetalles.length})
                </h3>
                <div className="space-y-2">
                  {activeDetalles.map((d) => (
                    <LineCard
                      key={d.id}
                      detalle={d}
                      cantidad={cantidades[d.id] ?? ''}
                      onCantidadChange={(next) =>
                        setCantidades((prev) => ({ ...prev, [d.id]: next }))
                      }
                      pedirSeries={detailQ.data.mueveStock !== false && d.llevaNroSerie}
                      series={seriesPorLinea[d.id] ?? []}
                      onSeriesChange={(next) =>
                        setSeriesPorLinea((prev) => ({ ...prev, [d.id]: next }))
                      }
                      onAnular={() => handleAnularRequest(d)}
                      readOnly={readOnly}
                      busy={busy}
                    />
                  ))}
                </div>
              </section>

              {anuladas.length > 0 && (
                <section className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Líneas anuladas ({anuladas.length})
                  </h3>
                  <div className="space-y-1">
                    {anuladas.map((d) => (
                      <div
                        key={d.id}
                        className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-1.5 text-sm text-muted-foreground line-through"
                      >
                        <span>{d.fields.Articulo_DR || '—'}</span>
                        <span className="text-xs">
                          {d.cantidadEsperada} un.
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <HistorialEntregas
                remitos={detailQ.data?.remitos ?? []}
                sinRemito={activeDetalles.reduce((n, d) => n + d.recibidoSinRemito, 0)}
              />

              {!readOnly && detailQ.data.mueveStock !== false && (
                <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>
                    Cada entrega pasa lo recibido de <em>Por Ingresar</em> a{' '}
                    <em>Disponible</em>. Las unidades que ya se vendieron mientras
                    viajaban siguen en su venta y no vuelven a Disponible. Al
                    finalizar, la recepción queda cerrada.
                  </span>
                </div>
              )}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cerrar
          </Button>
          {!readOnly && (
            <>
              <Button
                variant="outline"
                onClick={() => saveMutation.mutate(false)}
                disabled={!entregaLista || busy}
                title={
                  entregaLista
                    ? undefined
                    : hayError
                      ? 'Corregí lo marcado en rojo'
                      : 'Cargá el Nº de remito, la fecha y la cantidad de al menos una línea'
                }
                className="gap-2"
              >
                <Save className="h-4 w-4" aria-hidden />
                Registrar entrega
              </Button>
              <Button
                onClick={() => saveMutation.mutate(true)}
                disabled={busy || (hayEntrega && !entregaLista)}
                title={
                  hayEntrega && !entregaLista
                    ? hayError
                      ? 'Corregí lo marcado en rojo'
                      : 'Cargá el Nº de remito y la fecha de la entrega'
                    : undefined
                }
                className="gap-2"
              >
                <CheckCircle2 className="h-4 w-4" aria-hidden />
                {hayEntrega ? 'Registrar y finalizar' : 'Finalizar recepción'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Last-line guard. Backend auto-anulates the header when this is
          the last active line; we surface that here so the user knows
          it's about to happen rather than discovering it via the
          invalid_body that the old flow produced on Finalizar. */}
      {confirmAnular && (
        <Dialog
          open
          onOpenChange={(o) => !o && !anularMutation.isPending && setConfirmAnular(null)}
        >
          <DialogContent
            className="max-w-md"
            processing={anularMutation.isPending && 'Anulando recepción…'}
          >
            <DialogHeader>
              <DialogTitle>¿Anular la última línea?</DialogTitle>
              <DialogDescription>
                Es la única línea activa. Si la anulás, la recepción se marcará
                automáticamente como <strong>Anulada</strong> y no podrás
                finalizarla.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="text-sm">
              <div className="rounded-md border bg-muted/30 px-3 py-2">
                <div className="font-medium">
                  {confirmAnular.fields.Articulo_DR || '—'}
                </div>
                <div className="text-xs text-muted-foreground">
                  {confirmAnular.cantidadEsperada} un. esperado
                </div>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setConfirmAnular(null)}
                disabled={anularMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  const target = confirmAnular;
                  setConfirmAnular(null);
                  anularMutation.mutate(target.id);
                }}
                disabled={anularMutation.isPending}
                className="gap-2"
              >
                Anular y cerrar recepción
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

function HeaderSummary({ detalles }: { detalles: DetalleRow[] }) {
  const esperado = detalles.reduce((s, d) => s + d.cantidadEsperada, 0);
  const recibido = detalles.reduce((s, d) => s + d.cantidadRecibida, 0);
  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Esperado
        </div>
        <div className="text-base font-semibold tabular-nums">{esperado}</div>
      </div>
      <div className="h-8 w-px bg-border" />
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Recibido
        </div>
        <div
          className={cn(
            'text-base font-semibold tabular-nums',
            recibido === 0 && 'text-muted-foreground',
            recibido > 0 && recibido < esperado && 'text-amber-700',
            recibido >= esperado && esperado > 0 && 'text-emerald-700',
          )}
        >
          {recibido}
        </div>
      </div>
    </div>
  );
}

function LineCard({
  detalle,
  cantidad,
  onCantidadChange,
  pedirSeries,
  series,
  onSeriesChange,
  onAnular,
  readOnly,
  busy,
}: {
  detalle: DetalleRow;
  /** Cantidad de la entrega en curso (texto del input). */
  cantidad: string;
  onCantidadChange: (next: string) => void;
  /** Artículo con serie y recepción que mueve stock: se puede anotar la serie de cada unidad. */
  pedirSeries: boolean;
  /** Un valor por unidad de la entrega (posición = unidad). */
  series: string[];
  onSeriesChange: (next: string[]) => void;
  onAnular: () => void;
  readOnly: boolean;
  busy: boolean;
}) {
  const f = detalle.fields;
  const esperada = detalle.cantidadEsperada;
  const yaRecibida = detalle.cantidadRecibida;
  const pendiente = Math.max(0, esperada - yaRecibida);
  const entrega = parseNumber(cantidad);
  const totalConEntrega = yaRecibida + entrega;
  const error = entrega > 0 ? errorCantidad(entrega, esperada, yaRecibida) : null;
  const complete = !error && esperada > 0 && totalConEntrega >= esperada - 0.0001;
  const sinArticulo = !(f.ArticuloID_DR ?? '').trim() || f.ArticuloID_DR === '-';
  // Lo recibido ya movió stock: la línea no se puede anular (el server también lo frena).
  const conRecibido = yaRecibida > 0.0001;

  return (
    <div
      className={cn(
        'rounded-md border bg-card p-3 transition-colors',
        complete && 'border-emerald-300 bg-emerald-50/40',
        error && 'border-destructive/40 bg-destructive/5',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground">
            {detalle.modelo || f.Articulo_DR || '—'}
          </div>
          {detalle.modelo &&
            f.Articulo_DR &&
            detalle.modelo !== f.Articulo_DR && (
              <div className="text-xs text-muted-foreground">{f.Articulo_DR}</div>
            )}
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>
              Esperada:{' '}
              <span className="font-semibold tabular-nums text-foreground">{esperada}</span>
            </span>
            <span>
              Ya recibida:{' '}
              <span className="font-semibold tabular-nums text-foreground">{yaRecibida}</span>
            </span>
            <span>
              Pendiente:{' '}
              <span
                className={cn(
                  'font-semibold tabular-nums',
                  pendiente > 0 ? 'text-amber-700' : 'text-emerald-700',
                )}
              >
                {pendiente}
              </span>
            </span>
          </div>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={onAnular}
            disabled={busy || conRecibido}
            aria-label="Anular línea"
            title={
              conRecibido
                ? 'No se puede anular: la línea ya recibió mercadería'
                : 'Anular línea'
            }
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive disabled:opacity-50 disabled:hover:border-border disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </div>
      {sinArticulo && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Sin artículo asociado: recibirla no mueve stock.
        </p>
      )}
      {!readOnly && (
        <div className="mt-3 space-y-1">
          <Label htmlFor={`${detalle.id}-qty`} className="text-xs">
            Cantidad de esta entrega
          </Label>
          <Input
            id={`${detalle.id}-qty`}
            type="number"
            inputMode="numeric"
            min={0}
            max={pendiente}
            step={1}
            placeholder="0"
            value={cantidad}
            onChange={(e) => onCantidadChange(e.target.value)}
            className={cn(
              'tabular-nums',
              error && 'border-destructive bg-destructive/5',
              complete && 'border-emerald-400 bg-emerald-50',
            )}
            disabled={busy}
          />
          {error && <p className="text-[11px] font-medium text-destructive">{error}</p>}
          {entrega > 0 && !error && totalConEntrega < esperada - 0.0001 && (
            <p className="text-[11px] text-muted-foreground">
              Parcial — quedan {Math.round((esperada - totalConEntrega) * 100) / 100} por recibir.
            </p>
          )}
          {pedirSeries && entrega > 0 && !error && (
            <SeriesUnidades
              detalleId={detalle.id}
              unidades={entrega}
              series={series}
              onChange={onSeriesChange}
              disabled={busy}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Un campo por unidad de la entrega ("Unidad 1", "Unidad 2"...), plegable. Así
 * no se pueden cargar más series que unidades ni mezclar separadores; una serie
 * repetida en la misma línea queda en rojo y frena el envío.
 */
function SeriesUnidades({
  detalleId,
  unidades,
  series,
  onChange,
  disabled,
}: {
  detalleId: string;
  unidades: number;
  series: string[];
  onChange: (next: string[]) => void;
  disabled: boolean;
}) {
  const [abierto, setAbierto] = useState(false);
  const valores = seriesDeEntrega(series, unidades);
  const cargadas = valores.filter((v) => v.trim()).length;
  const repetidas = seriesRepetidas(valores);
  const set = (i: number, v: string) => {
    const next = [...valores];
    next[i] = v;
    onChange(next);
  };

  return (
    <div className="mt-2 rounded-md border bg-background">
      <button
        type="button"
        onClick={() => setAbierto((o) => !o)}
        aria-expanded={abierto}
        aria-controls={`${detalleId}-series`}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs"
      >
        <span className="font-medium text-foreground">
          Nº de serie <span className="font-normal text-muted-foreground">· opcional</span>
        </span>
        <span className="flex items-center gap-2">
          <span
            className={cn(
              'tabular-nums',
              repetidas.size > 0
                ? 'font-medium text-destructive'
                : cargadas === unidades
                  ? 'text-emerald-700'
                  : 'text-muted-foreground',
            )}
          >
            {repetidas.size > 0 ? 'Serie repetida' : `${cargadas} de ${unidades} cargadas`}
          </span>
          <ChevronDown
            className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', abierto && 'rotate-180')}
            aria-hidden
          />
        </span>
      </button>
      {abierto && (
        <div id={`${detalleId}-series`} className="space-y-2 border-t px-3 py-2">
          <div className="max-h-60 space-y-1.5 overflow-y-auto pr-1">
            {valores.map((v, i) => {
              const repetida = repetidas.has(v.trim().toLowerCase());
              return (
                <div key={i} className="flex items-center gap-2">
                  <Label
                    htmlFor={`${detalleId}-serie-${i}`}
                    className="w-16 shrink-0 text-[11px] font-normal text-muted-foreground"
                  >
                    Unidad {i + 1}
                  </Label>
                  <Input
                    id={`${detalleId}-serie-${i}`}
                    value={v}
                    onChange={(e) => set(i, e.target.value)}
                    placeholder="Nº de serie"
                    autoComplete="off"
                    className={cn('h-8 text-sm', repetida && 'border-destructive bg-destructive/5')}
                    aria-invalid={repetida || undefined}
                    disabled={disabled}
                  />
                </div>
              );
            })}
          </div>
          {repetidas.size > 0 && (
            <p className="text-[11px] font-medium text-destructive">
              Hay series repetidas: cada unidad lleva una serie distinta.
            </p>
          )}
          <p className="text-[11px] text-muted-foreground">
            Cada unidad queda en el stock con su serie, su compra y su importación. Las que dejes
            vacías se cargan al despachar.
          </p>
        </div>
      )}
    </div>
  );
}

/** Los campos de serie de la entrega: uno por unidad (se recorta o completa con vacíos). */
function seriesDeEntrega(series: string[] | undefined, unidades: number): string[] {
  const n = Number.isInteger(unidades) && unidades > 0 ? unidades : 0;
  return Array.from({ length: n }, (_, i) => series?.[i] ?? '');
}

/** Series (en minúscula, sin espacios a los lados) que aparecen más de una vez. */
function seriesRepetidas(valores: string[]): Set<string> {
  const vistas = new Set<string>();
  const repetidas = new Set<string>();
  for (const v of valores) {
    const k = v.trim().toLowerCase();
    if (!k) continue;
    if (vistas.has(k)) repetidas.add(k);
    vistas.add(k);
  }
  return repetidas;
}

function HistorialEntregas({
  remitos,
  sinRemito,
}: {
  remitos: RemitoRow[];
  /** Total recibido antes de que existieran los remitos. */
  sinRemito: number;
}) {
  if (remitos.length === 0 && sinRemito <= 0) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Entregas ({remitos.length})
      </h3>
      <div className="space-y-2">
        {remitos.map((r) => (
          <div key={r.id} className="rounded-md border bg-card px-3 py-2 text-sm">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium">Remito {r.nroRemito}</span>
              <span className="text-xs text-muted-foreground">
                {fmtFecha(r.fecha)}
                {r.usuario && ` · ${r.usuario}`}
              </span>
            </div>
            <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              {r.lineas.map((l) => (
                <li key={l.detalleId} className="flex flex-wrap justify-between gap-2">
                  <span>{l.articulo || '—'}</span>
                  <span className="tabular-nums">
                    <span className="font-semibold text-foreground">{l.cantidad}</span> un.
                    {l.movida < l.cantidad - 0.0001 && (
                      <span className="text-amber-700">
                        {' '}
                        · {l.movida} pasaron a Disponible
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {sinRemito > 0 && (
          <p className="text-xs text-muted-foreground">
            {sinRemito} un. recibidas antes de que se registraran remitos (sin detalle de
            entrega).
          </p>
        )}
      </div>
    </section>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === 'Pendiente'
      ? 'bg-amber-100 text-amber-700'
      : status === 'Recibido'
        ? 'bg-emerald-100 text-emerald-700'
        : status === 'Anulado'
          ? 'bg-rose-100 text-rose-700'
          : 'bg-secondary text-foreground';
  return (
    <span
      className={cn(
        'inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
        cls,
      )}
    >
      {status || '—'}
    </span>
  );
}

/**
 * Misma regla que el server (`errorCantidadEntrega` en recepcionStock.ts): unidades
 * enteras y no más de lo que falta recibir. null = válida.
 */
function errorCantidad(cantidad: number, esperada: number, yaRecibida: number): string | null {
  if (cantidad <= 0) return null;
  if (!Number.isInteger(cantidad)) return 'Las cantidades van en unidades enteras.';
  const pendiente = Math.max(0, Math.round((esperada - yaRecibida) * 100) / 100);
  if (cantidad > pendiente + 0.0001) {
    return pendiente > 0
      ? `Quedan ${pendiente} por recibir: no se puede recibir más.`
      : 'Esta línea ya está recibida completa.';
  }
  return null;
}

function parseNumber(raw: string): number {
  const trimmed = raw.trim();
  if (!trimmed) return 0;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Hoy en fecha local como YYYY-MM-DD (lo que espera <input type="date">). */
function hoyLocalIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function fmtFecha(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}
